<template>
  <!-- Hero Banner Journal Section -->
  <section class="relative mb-2">
    <template v-if="mediaImage">
      <!-- desktop -->
      <UiArticleMediaImage v-if="mediaImage" class="hidden md:block" :altText="altText" :src="mediaImage.image.url"
        :title="article.title" :overlaytop="false" :overlaybottom="false" width="1440" height="900"
        :sizes="'md:1024px lg:1280px xl:1535 2xl:2500'" :modifiers="modifiers" />

      <!-- mobile 375x464 -->
      <UiArticleMediaImage v-if="mediaImage" class="block md:hidden" :altText="altText" :src="mediaImage.image.url"
        :title="article.title" :overlaytop="false" :overlaybottom="false" width="375" height="464"
        :sizes="'412px sm:640px'" :modifiers="modifiers" />
    </template>
    <UiArticleImage v-else :fit="'crop'" :modifiers="modifiers" v-if="article.image" :article="article" />
    <UiContentwrapper class="contentwrapper absolute flex justify-center items-center inset-0">
      <UiNarrowColumnLeft v-if="article.excerpt">
        <h1 class="m-0 text-white drop-shadow-lg" v-html="article.title"></h1>
      </UiNarrowColumnLeft>
    </UiContentwrapper>
  </section>

  <!-- Article Content Section -->
  <div class="relative">
    <div class="mt-24">
      <div v-if="article">
        <div class="htmlcontent" v-html="theHtml"></div>

        <!-- Article info -->
        <UiExtraNarrowContent>
          <div class="grid grid-cols-1 sm:grid-cols-3 items-center gap-y-2 sm:gap-y-0">
            <!-- Column 1: Published date -->
            <div class="flex items-center">
              <p class="text-xs sm:mt-5 ">
                Published on
                {{ new Date(article.publishedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                }) }}
              </p>
            </div>

            <!-- Column 2: Words by -->
            <div class="flex items-center">
              <p class="text-xs sm:mt-5 " v-if="article.wordsBy">
                Words by: {{ article.wordsBy }}
              </p>
            </div>

            <!-- Column 3: Share button -->
            <div class="flex justify-start sm:justify-end items-center">
              <UiShareButton :article="article">
                Share
                <IconShare class="inline-block h-5 w-5 ml-1" stroke="1.25" />
              </UiShareButton>
            </div>
          </div>
        </UiExtraNarrowContent>
      </div>
    </div>
  </div>

  <UiExtraNarrowContent>
    <NuxtLink to="/blogs/journal">
      <UiButton class="btn lightbrown text-white px-8 py-4 rounded-md mt-12 mb-16">
        Back to all blog posts
      </UiButton>
    </NuxtLink>
  </UiExtraNarrowContent>

  <ContentFeaturedPages v-if="homepagedata.featuredPages" :page="homepagedata" />

</template>

<script setup>
import { computed } from 'vue';
import { useBlogStore } from '@/stores/blogStore';
import { usePageStore } from '@/stores/pageStore';

import { useImageUtils } from '@/composables/useImageUtils';
import { IconShare } from '@tabler/icons-vue';
const { toImgixPath } = useImageUtils();

const blogStore = useBlogStore();
const pageStore = usePageStore();
// Props
const props = defineProps({
  article: {
    type: Object,
    required: true,
  },
});

const mediaImage = computed(() => props.article.coverImage?.reference || null);

// Detect small screen size
const updateScreenSize = () => {
  isSmallScreen.value = window.matchMedia('(max-width: 768px)').matches;
};

onMounted(() => {
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize);
});

const altText = computed(() => {
  return mediaImage.value?.image.altText || props.article.title || 'Gravaa image';
});

const theHtml = computed(() => {
  const updatedHtml = props.article.contenthtml.replace(/<img[^>]+src="([^"]+)"[^>]*>/g, (match, url) => {
    const newSrc = toImgixPath(url);
    ;
    let width = 1440;
    if (isSmallScreen.value) {
      width = 412; // Use smaller width for small screens
    }
    return match.replace(url, 'https://gravaa-imgix.imgix.net/' + newSrc + '?auto=format,compress&w=' + width);
  });
  return updatedHtml;

});

// Hero image modifier logic
const modifiers = computed(() => {

  if (isSmallScreen.value) {
    return {
      auto: 'format,compress',
      ar: '16:20', // Aspect ratio for small screens
      'fp-x': mediaImage.value?.presentation?.asJson.focalPoint.x ?? 0.5,
      'fp-y': mediaImage.value?.presentation?.asJson.focalPoint.y ?? 0.5,
    };
  } else if (props.article.tags && blogStore.articleHasTag(props.article, 'Tall')) {
    return {
      auto: 'format,compress',
      ar: '1440:690',
      'fp-x': mediaImage.value?.presentation?.asJson.focalPoint.x ?? 0.5,
      'fp-y': mediaImage.value?.presentation?.asJson.focalPoint.y ?? 0.5,
    };
  } else {
    return {
      auto: 'format,compress',
      ar: '1440:495',
      'fp-x': mediaImage.value?.presentation?.asJson.focalPoint.x ?? 0.5,
      'fp-y': mediaImage.value?.presentation?.asJson.focalPoint.y ?? 0.5,
    };
  }


});

const isSmallScreen = ref(false);

const { data: homepagedata } = await useAsyncData('homepage-journal', async () => {
  return await pageStore.fetchPageByHandle('home');
});
</script>

<style>
@reference "../../assets/css/main.css";

/* Hero Banner h1 style */
section .contentwrapper h1 {
  @apply text-7xl;
}

@media (max-width: 640px) {
  section .contentwrapper h1 {
    @apply text-3xl;
  }
}

@media (max-width: 640px) {
  section.relative.mb-2 img {
    aspect-ratio: 4/5 !important;
    height: auto;
    max-height: 140vw;
    object-fit: cover;
  }
}


/* Photo credit */
.photo-credit {
  display: block;
  margin-top: 10px;

  font-size: 0.75rem;
  color: #1C1C1C;
  max-width: 72rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 0;
  text-align: left;
}

/* === SHARED BASE IMAGE RULES === */
.htmlcontent div {
  display: flex;
  flex-direction: row;
}

.htmlcontent div img {
  width: 100%;
  max-width: 100%;
  object-fit: cover;
  @apply max-w-6xl mx-auto;
}

/* === TWO-IMAGE <div> === */
.htmlcontent div:has(img:first-child + img:last-child) {
  display: flex;
  gap: 28px;
  justify-content: center;
  margin-top: 76px;
  margin-bottom: 10px;
  @apply max-w-6xl mx-auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  /* NO flex-wrap here so images stay side by side on large screens */
}

/* Limit each image inside 2-image <div> to 560px */
.htmlcontent div:has(img:first-child + img:last-child) img {
  max-width: 560px;
  width: 100%;
  height: auto;
  object-fit: cover;
}

/* === RESPONSIVE ADJUSTMENTS FOR SMALL SCREENS === */
@media (max-width: 640px) {
  .htmlcontent div:has(img:first-child + img:last-child) {
    flex-wrap: wrap;
    /* Allow wrapping on small screens */
  }

  .htmlcontent div:has(img:first-child + img:last-child) img {
    max-width: 100% !important;
    /* Make images take full width on small screens */
    width: 100% !important;
  }
}


/* Limit each image inside 2-image <div> to 560px */
.htmlcontent div:has(img:first-child + img:last-child) img {
  max-width: 560px;
  width: 100%;
  height: auto;
  object-fit: cover;
}

/* === THREE-IMAGE <div> === */
.htmlcontent div:has(img:nth-child(1) + img:nth-child(2) + img:nth-child(3):last-child) {
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 76px;
  margin-bottom: 76px;
  @apply max-w-6xl mx-auto;
}

.htmlcontent div:has(img:nth-child(1) + img:nth-child(2) + img:nth-child(3):last-child) img {
  width: calc(33.333% - 1px);
  max-width: 370px;
  height: auto;
  object-fit: cover;
}

/* === SINGLE IMAGE IN <p> === */
.htmlcontent p:has(img:only-child) {
  max-width: 48rem;
  text-align: center;
  margin-top: 76px;
  margin-bottom: 76px;
  margin-left: auto;
  margin-right: auto;
}

.htmlcontent p:has(img:only-child) img {
  width: 100%;
  height: auto;
  display: block;
}

/* === TWO IMAGES IN <p> === */
.htmlcontent p:has(img:first-child + img:last-child) {
  display: flex;
  gap: 28px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  max-width: 48rem;
  text-align: center;
  margin: 76px auto;
}

.htmlcontent p:has(img:first-child + img:last-child) img {
  width: calc(50% - 14px);
  max-width: 400px;
  height: auto;
  object-fit: cover;
}

/* === TYPOGRAPHY === */
.htmlcontent h1,
.htmlcontent h2,
.htmlcontent h3,
.htmlcontent h4,
.htmlcontent h5,
.htmlcontent p {
  @apply max-w-3xl px-11 mx-auto;
}

.htmlcontent h1,
.htmlcontent h2,
.htmlcontent h3,
.htmlcontent h4,
.htmlcontent h5 {
  @apply mt-4;
}

/* === BUTTON === */
.btn {
  transition: transform 0.2s ease-in-out;
}

.btn:hover {
  transform: scale(1.03);
}

/* === VIDEO EMBEDS === */
.htmlcontent p:has(iframe:only-child),
.htmlcontent div:has(iframe:only-child) {
  max-width: 72rem;
  margin: 76px auto;
  text-align: center;
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

.htmlcontent p:has(iframe:only-child) iframe,
.htmlcontent div:has(iframe:only-child) iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  object-fit: fill;
}

/* === RESPONSIVE VIDEO MARGINS === */
@media (max-width: 640px) {

  .htmlcontent p:has(iframe:only-child),
  .htmlcontent div:has(iframe:only-child) {
    margin: 40px 1rem;
  }
}

@media (min-width: 640px) and (max-width: 1024px) {

  .htmlcontent p:has(iframe:only-child),
  .htmlcontent div:has(iframe:only-child) {
    margin: 70px auto;
  }
}
</style>
